import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { CONFIG } from '../config';

const fetcher = async (url) => {
  const res = await fetch(url);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message = data?.message || data?.error || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

/**
 * Détermine l'URL de l'API en fonction du type de station
 */
const getApiUrl = (station) => {
  if (station.id === 'RER_Chessy') {
    return `${CONFIG.API_URL}/api/transport/chessy/rer-a`;
  }
  if (station.id === 'SNCF_Chessy' || station.type === 'SNCF') {
    return `${CONFIG.API_URL}/api/transport/chessy/sncf`;
  }
  return null;
};

/**
 * Formatte une heure au format HH:mm
 */
const formatTime = (date) => {
  if (!date) return '--:--';
  return new Date(date).toLocaleTimeString('fr-FR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * Composant d'affichage alterné (heure + retard/status) style panneau à palettes SNCF
 * Alterne entre: heure + retard (si présent) puis heure + status
 */
const AlternatingDisplay = ({ time, status, delayMinutes }) => {
  const [showDelay, setShowDelay] = useState(true);
  const [animating, setAnimating] = useState(false);

  // Déterminer s'il y a un retard à afficher
  const hasDelay = delayMinutes && delayMinutes > 0;

  useEffect(() => {
    // Si pas de retard, pas besoin d'alternance
    if (!hasDelay) return;

    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setShowDelay(prev => !prev);
        setTimeout(() => {
          setAnimating(false);
        }, 150);
      }, 150);
    }, 5000);

    return () => clearInterval(interval);
  }, [hasDelay]);

  // Mapper les statuts vers des labels français
  const getStatusLabel = (s) => {
    if (!s) return 'En circulation';
    const statusMap = {
      'onTime': 'À l\'heure',
      'ontime': 'À l\'heure',
      'scheduled': 'À l\'heure',
      'delayed': 'Retardé',
      'late': 'En retard',
      'cancelled': 'Supprimé',
      'canceled': 'Supprimé',
      'cancelled-by-control-centre': 'Supprimé',
      'boarding': 'Embarquement',
      'departed': 'Parti',
      'arrived': 'Arrivé'
    };
    return statusMap[String(s).toLowerCase()] || s;
  };

  // Formater le retard
  const getDelayLabel = (d) => {
    if (!d || d <= 0) return 'À l\'heure';
    return `+${d} min`;
  };

  // Déterminer la classe de couleur selon le statut
  const getStatusColorClass = (s) => {
    if (!s) return 'status-default';
    const statusStr = String(s).toLowerCase();
    if (['ontime', 'onTime', 'scheduled'].includes(statusStr)) return 'status-ontime';
    if (['delayed', 'late'].includes(statusStr)) return 'status-delayed';
    if (['cancelled', 'canceled', 'cancelled-by-control-centre'].includes(statusStr)) return 'status-cancelled';
    return 'status-default';
  };

  // Si pas de retard, afficher seulement l'heure avec le statut
  // Si retard, alterner entre heure+retard et heure+statut
  const secondaryValue = !hasDelay
    ? getStatusLabel(status)
    : showDelay
      ? getDelayLabel(delayMinutes)
      : getStatusLabel(status);

  const secondaryClass = !hasDelay
    ? `display-status ${getStatusColorClass(status)}`
    : showDelay
      ? 'display-delay'
      : `display-status ${getStatusColorClass(status)}`;

  return (
    <div className={`alternating-display ${animating ? 'animating' : ''} ${hasDelay ? 'has-delay' : ''}`}>
      <div className="display-content">
        <span className="display-time">{time}</span>
        <span className={`display-secondary ${secondaryClass}`}>
          {secondaryValue}
        </span>
      </div>
    </div>
  );
};

/**
 * Composant d'affichage des prochains passages dans le popup de la carte
 * Supporte RER A (IDFM) et SNCF (TGV, OUIGO, TER, Eurostar)
 */
const TransportPopup = ({ station }) => {
  const apiUrl = getApiUrl(station);
  
  const { data, error, isLoading } = useSWR(
    apiUrl,
    fetcher,
    { 
      refreshInterval: 60000, // Refresh every minute
      revalidateOnFocus: true 
    }
  );

  if (isLoading) {
    return (
      <div className="transport-popup-content">
        <h3 className="popup-title">{station.name}</h3>
        <div className="transport-loading">
          <div className="transport-spinner"></div>
          <p>Chargement des horaires...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transport-popup-content">
        <h3 className="popup-title">{station.name}</h3>
        <div className="transport-error">
          <p>❌ Impossible de charger les horaires</p>
          <a 
            href={station.iframeUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="transport-btn"
          >
            Voir sur le site officiel ↗
          </a>
        </div>
      </div>
    );
  }

  const passages = data?.data?.passages || [];

  const getMinutesUntil = (p) => {
    if (typeof p?.waitingMinutes === 'number' && p.waitingMinutes >= 0) {
      return p.waitingMinutes;
    }

    if (p?.expectedTime) {
      const ts = new Date(p.expectedTime).getTime();
      if (!Number.isNaN(ts)) {
        const deltaMs = ts - Date.now();
        return Math.max(0, Math.round(deltaMs / 60000));
      }
    }

    return Number.POSITIVE_INFINITY;
  };

  const getStatusClassName = (status) => {
    if (status === null || status === undefined) return 'unknown';
    const str = String(status).trim();
    if (!str) return 'unknown';
    return str.toLowerCase().replace(/\s+/g, '-');
  };

  const isSncf = station.type === 'SNCF' || station.id === 'SNCF_Chessy';
  
  // SNCF: 10 trains (5 par colonne) avec alternance retard/statut, RER: 4 passages
  const maxPassages = isSncf ? 10 : 4;
  
  const nextPassages = [...passages]
    .sort((a, b) => getMinutesUntil(a) - getMinutesUntil(b))
    .slice(0, maxPassages);

  const hasPassages = nextPassages.length > 0;

  const uniqueDestinations = Array.from(
    new Set(nextPassages.map((p) => p?.destination).filter(Boolean))
  );
  const isMultiDestination = uniqueDestinations.length > 1;

  const normalizeKnownValue = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    if (str.toLowerCase() === 'unknown') return null;
    return str;
  };

  return (
    <div className="transport-popup-content">
      <h3 className="popup-title">{station.name}</h3>
      <div 
        className="transport-badge" 
        style={{ backgroundColor: station.color }}
      >
        {station.type} {station.line}
      </div>
      
      {hasPassages ? (
        <div className="transport-passages">
          <ul className={`passages-list ${isSncf ? 'passages-list-grid' : ''}`}>
            {nextPassages.map((passage, idx) => {
              const trainNumber = normalizeKnownValue(passage?.trainNumber);
              const platform = normalizeKnownValue(passage?.platform);
              const hasMeta = Boolean(trainNumber || platform);

              return (
                <li key={idx} className="passage-item">
                  <div className="passage-main">
                    {/* Pour SNCF: toujours afficher la destination */}
                    {/* Pour RER: afficher uniquement si multi-destination */}
                    {(isSncf || isMultiDestination) && (
                      <div className="passage-destination">
                        {passage.destination || 'Destination inconnue'}
                      </div>
                    )}
                    <div className="passage-sub">
                      {/* Pour RER: afficher le status */}
                      {/* Pour SNCF: le network/status est dans l'affichage alterné */}
                      {!isSncf && (
                        <span 
                          className={`passage-status status-${getStatusClassName(passage?.status)}`}
                          title={passage.status}
                        >
                          {passage.status}
                        </span>
                      )}
                      {hasMeta && (
                        <div className="passage-meta">
                          {trainNumber && (
                            <span className="passage-meta-item">Train {trainNumber}</span>
                          )}
                          {platform && (
                            <span className="passage-meta-item">Quai {platform}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="passage-time">
                      {/* Pour SNCF: affichage alterné heure/retard puis heure/statut style panneau à palettes */}
                      {isSncf ? (
                        <AlternatingDisplay 
                          time={formatTime(passage.expectedTime)}
                          status={passage.status}
                          delayMinutes={passage.delayMinutes}
                        />
                      ) : (
                        /* Pour RER: affichage classique */
                        passage.waitingMinutes !== null 
                          ? `${passage.waitingMinutes} min`
                          : formatTime(passage.expectedTime)
                      )}
                    </span>
                </li>
              );
            })}
          </ul>
          <p className="transport-updated">
            Mis à jour: {data?.data?.responseTimestamp 
              ? new Date(data.data.responseTimestamp).toLocaleTimeString('fr-FR')
              : '--:--'
            }
            {(data?.meta?.cached || data?.data?.cached) && ' (cache)'}
          </p>
        </div>
      ) : (
        <div className="transport-no-data">
          <p>🚫 Aucun départ disponible actuellement</p>
          <p className="transport-no-data-sub">
            {isSncf 
              ? 'Aucun train SNCF prévu prochainement'
              : 'Le RER A ne circule peut-être pas à cette heure'
            }
          </p>
        </div>
      )}
      
      <div className="transport-actions">
        <a 
          href={station.iframeUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="transport-btn secondary"
        >
          Plus de détails ↗
        </a>
      </div>
    </div>
  );
};

export default TransportPopup;
